import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowDownTrayIcon, ClipboardDocumentIcon, DocumentArrowUpIcon, ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/24/outline'
import {
  validateNewsImport, listReleaseDrafts, markReleaseDraftReleased, unmarkReleaseDraftReleased, apiError,
  type NewsImportError, type NewsImportItem, type ReleaseDraft, type ReleaseDraftList, type ReleaseDraftRef,
} from '../../api/news'

// "Import" in the composer: paste a news item as JSON (a full item or just a
// blocks array; docs/news-api.md section 10), checked by the server's real
// save rules, then handed to the composer to fill in. "Copy as JSON" opens
// this in export mode only when the clipboard is unavailable.

const btn = 'inline-flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-semibold rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-slate-800 disabled:opacity-50'
const btnPrimary = 'inline-flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-semibold rounded-lg bg-blue-600 border border-blue-600 text-white hover:bg-blue-700 disabled:opacity-50'

const PLACEHOLDER = `{
  "title": "Check your week before payroll",
  "summary": "A Week review card shows on the last day of your week.",
  "category": "release",
  "blocks": [
    { "type": "paragraph", "text": "Fix a time or mark it **right**." },
    { "type": "button", "label": "Open week review", "target": "app:week_review" }
  ]
}`

interface Props {
  /** 'export' shows `exportText` read-only with a Copy button. */
  mode: 'import' | 'export'
  exportText?: string
  onClose: () => void
  /** `draft` is the release draft the text came from (publishing then marks it released). */
  onImport: (item: NewsImportItem, ignored: string[], draft: ReleaseDraftRef | null) => void
}

const linkBtn = 'text-[12px] font-semibold text-blue-700 hover:underline disabled:opacity-50'

function ageText(d: ReleaseDraft): string {
  if (d.ageDays == null) return `updated ${new Date(d.updatedAt).toLocaleString()}`
  if (d.ageDays === 0) return 'today'
  return d.ageDays === 1 ? '1 day ago' : `${d.ageDays} days ago`
}

export default function NewsImportDialog({ mode, exportText = '', onClose, onImport }: Props) {
  const [text, setText] = useState(mode === 'export' ? exportText : '')
  const [busy, setBusy] = useState(false)
  const [errors, setErrors] = useState<NewsImportError[]>([])
  const [copied, setCopied] = useState(false)
  const area = useRef<HTMLTextAreaElement>(null)
  const fileInput = useRef<HTMLInputElement>(null)
  const [fileName, setFileName] = useState<string | null>(null)
  // release-news/*.json shipped with the server: pick one after a deploy.
  // Released and old ones are hidden unless "Show released and old".
  const [list, setList] = useState<ReleaseDraftList | null>(null)
  const [showAll, setShowAll] = useState(false)
  const [draftBusy, setDraftBusy] = useState<string | null>(null)
  const [draftError, setDraftError] = useState<string | null>(null)
  const [picked, setPicked] = useState<ReleaseDraftRef | null>(null)
  const loadDrafts = useCallback(async (all: boolean) => {
    try { setList(await listReleaseDrafts(all)) } catch { setList({ tracking: false, maxAgeDays: 10, drafts: [] }) }
  }, [])
  useEffect(() => {
    if (mode !== 'import') return
    let live = true
    listReleaseDrafts(showAll)
      .then((d) => { if (live) setList(d) })
      .catch(() => { if (live) setList({ tracking: false, maxAgeDays: 10, drafts: [] }) })
    return () => { live = false }
  }, [mode, showAll])
  const drafts = list?.drafts ?? []
  const pickDraft = (d: ReleaseDraft) => {
    setText(d.json)
    setFileName(d.file)
    setPicked({ file: d.file, hash: d.hash })
    setErrors(d.error ? [{ field: null, message: `${d.file}: ${d.error}` }] : [])
  }
  const setReleased = async (d: ReleaseDraft, released: boolean) => {
    setDraftBusy(d.file)
    setDraftError(null)
    try {
      if (released) await markReleaseDraftReleased({ file: d.file, hash: d.hash })
      else await unmarkReleaseDraftReleased({ file: d.file, hash: d.hash })
      await loadDrafts(showAll)
    } catch (err) {
      setDraftError(apiError(err, released ? 'Could not mark it released' : 'Could not undo'))
    } finally {
      setDraftBusy(null)
    }
  }

  // A .json file (e.g. release-news/next-app.json) fills the box, same as pasting it.
  const readFile = (file: File | undefined) => {
    if (!file) return
    if (file.size > 1_000_000) { setErrors([{ field: null, message: 'That file is too big for a news item (over 1 MB).' }]); return }
    const reader = new FileReader()
    reader.onload = () => {
      const body = String(reader.result ?? '')
      setText(body)
      setFileName(file.name)
      // The same text as a listed draft counts as importing that draft.
      const same = drafts.find((d) => d.json === body)
      setPicked(same ? { file: same.file, hash: same.hash } : null)
      setErrors([])
    }
    reader.onerror = () => setErrors([{ field: null, message: `Could not read ${file.name}` }])
    reader.readAsText(file)
  }

  useEffect(() => {
    area.current?.focus()
    if (mode === 'export') area.current?.select()
  }, [mode])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !busy) onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [busy, onClose])

  const check = async () => {
    setBusy(true)
    setErrors([])
    try {
      const out = await validateNewsImport(text)
      if (out.ok) onImport(out.item, out.ignored, picked)
      else setErrors(out.errors.length ? out.errors : [{ field: null, message: out.error }])
    } catch (err) {
      setErrors([{ field: null, message: apiError(err, 'Could not check the JSON') }])
    } finally {
      setBusy(false)
    }
  }

  const copy = async () => {
    area.current?.select()
    try { await navigator.clipboard.writeText(text); setCopied(true) } catch { /* selected: Ctrl/Cmd+C works */ }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 backdrop-blur-sm p-4 overflow-y-auto" onClick={() => !busy && onClose()}>
      <div role="dialog" aria-modal="true" aria-labelledby="news-import-title" className="bg-white rounded-xl shadow-2xl border border-slate-200 w-full max-w-2xl my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-200">
          <h2 id="news-import-title" className="text-[15px] font-semibold text-slate-900 inline-flex items-center gap-2">
            {mode === 'import' ? <ArrowDownTrayIcon className="w-5 h-5 text-slate-500" /> : <ClipboardDocumentIcon className="w-5 h-5 text-slate-500" />}
            {mode === 'import' ? 'Import news from JSON' : 'This draft as JSON'}
          </h2>
          <button type="button" className="text-slate-400 hover:text-slate-700" aria-label="Close" disabled={busy} onClick={onClose}><XMarkIcon className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-3">
          {mode === 'import' ? (
            <p className="text-[13px] text-slate-600 leading-relaxed">
              Choose the .json file, or paste a full item or just a <code className="text-[12px] bg-slate-100 rounded px-1">blocks</code> array. Only <code className="text-[12px] bg-slate-100 rounded px-1">blocks</code> is required.
              The fields it sets (title, summary, category, version, cover, content, where, roles, platforms, pin, banner, push, email) replace the composer's; companies and times stay as they are.
              Nothing is saved until you save or publish.
            </p>
          ) : (
            <p className="text-[13px] text-slate-600">Copy this and paste it back with Import to make a copy or round-trip an edit.</p>
          )}
          {mode === 'import' && list && drafts.length > 0 && (
            <div className="rounded-lg border border-slate-200">
              <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-slate-100">
                <span className="text-[11px] font-bold uppercase tracking-wide text-slate-500">From this release</span>
                {list.tracking && (
                  <button type="button" className={linkBtn} onClick={() => setShowAll((v) => !v)}>
                    {showAll ? 'Hide released and old' : 'Show released and old'}
                  </button>
                )}
              </div>
              <ul className="divide-y divide-slate-100">
                {drafts.map((d) => (
                  <li key={`${d.file}:${d.hash}`} className={`flex items-stretch ${fileName === d.file ? 'bg-blue-50' : ''}`}>
                    <button type="button" disabled={busy} onClick={() => pickDraft(d)}
                      className="flex-1 min-w-0 text-left px-3 py-2 hover:bg-slate-50">
                      <div className="flex items-center justify-between gap-2">
                        <span className={`text-[13px] font-semibold truncate ${d.state === 'new' ? 'text-slate-900' : 'text-slate-500'}`}>{d.title || d.file}</span>
                        <span className="text-[11px] text-slate-500 shrink-0">{d.audience === 'portal' ? 'Admin portal' : d.audience === 'app' ? 'App' : d.audience || ''}{d.version ? ` · v${d.version}` : ''}</span>
                      </div>
                      <div className="text-[12px] text-slate-500 truncate">{d.error ? <span className="text-red-600">{d.error}</span> : d.summary || d.file}</div>
                      <div className="text-[11px] text-slate-400">{d.file} · {ageText(d)}</div>
                    </button>
                    {list.tracking && (
                      <div className="shrink-0 flex flex-col items-end justify-center gap-1 px-3 py-2">
                        {d.released ? (
                          <>
                            <span className="text-[11px] font-semibold rounded-full px-2 py-0.5 bg-emerald-50 text-emerald-700">
                              {d.released.newsId
                                ? <Link to={`/announcements/${d.released.newsId}`} target="_blank" rel="noreferrer" className="hover:underline">Released</Link>
                                : 'Released'}
                            </span>
                            <button type="button" className={linkBtn} disabled={draftBusy === d.file} onClick={() => setReleased(d, false)}>Undo</button>
                          </>
                        ) : (
                          <>
                            {d.old && <span className="text-[11px] font-semibold rounded-full px-2 py-0.5 bg-slate-100 text-slate-600">Older than {list.maxAgeDays} days</span>}
                            {d.state === 'hidden' && <span className="text-[11px] font-semibold rounded-full px-2 py-0.5 bg-slate-100 text-slate-600">Hidden</span>}
                            <button type="button" className={linkBtn} disabled={draftBusy === d.file} onClick={() => setReleased(d, true)}>Mark released</button>
                          </>
                        )}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
              {draftError && <div role="alert" className="px-3 py-2 text-[12px] text-red-700 border-t border-slate-100">{draftError}</div>}
            </div>
          )}
          {mode === 'import' && list?.tracking && drafts.length === 0 && (
            <div className="flex justify-end">
              <button type="button" className={linkBtn} onClick={() => setShowAll((v) => !v)}>
                {showAll ? 'Hide released and old' : 'Show released and old'}
              </button>
            </div>
          )}
          {mode === 'import' && (
            <div className="flex flex-wrap items-center gap-2">
              <input ref={fileInput} type="file" accept=".json,application/json" className="hidden"
                onChange={(e) => { readFile(e.target.files?.[0]); e.target.value = '' }} />
              <button type="button" className={btn} disabled={busy} onClick={() => fileInput.current?.click()}>
                <DocumentArrowUpIcon className="w-4 h-4" />Choose a .json file
              </button>
              <span className="text-[12.5px] text-slate-500">{fileName ? `Loaded ${fileName}` : 'or paste it below, or drop the file on the box'}</span>
            </div>
          )}
          <textarea ref={area} value={text} readOnly={mode === 'export'} spellCheck={false}
            onDragOver={(e) => { if (mode === 'import') e.preventDefault() }}
            onDrop={(e) => { if (mode === 'import' && e.dataTransfer.files?.length) { e.preventDefault(); readFile(e.dataTransfer.files[0]) } }}
            onChange={(e) => { setText(e.target.value); setFileName(null); setPicked(null); if (errors.length) setErrors([]) }}
            placeholder={mode === 'import' ? PLACEHOLDER : undefined} aria-label="JSON"
            className="w-full h-72 resize-y rounded-lg border border-slate-200 px-3 py-2 font-mono text-[12.5px] leading-relaxed text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500" />
          {errors.length > 0 && (
            <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-[13px] text-red-700">
              <div className="font-semibold inline-flex items-center gap-1.5 mb-1"><ExclamationTriangleIcon className="w-4 h-4" />{errors.length === 1 ? 'Fix this and try again' : `Fix these ${errors.length} things and try again`}</div>
              <ul className="list-disc pl-5 space-y-0.5 max-h-48 overflow-auto">
                {errors.map((e, i) => <li key={i} className="break-words">{e.message}</li>)}
              </ul>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-100">
          <button type="button" className={btn} disabled={busy} onClick={onClose}>{mode === 'export' ? 'Close' : 'Cancel'}</button>
          {mode === 'import'
            ? <button type="button" className={btnPrimary} disabled={busy || !text.trim()} onClick={check}>{busy ? 'Checking…' : 'Check and fill in'}</button>
            : <button type="button" className={btnPrimary} onClick={copy}><ClipboardDocumentIcon className="w-4 h-4" />{copied ? 'Copied' : 'Copy'}</button>}
        </div>
      </div>
    </div>
  )
}
